import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

// Helper function to format dates
function formatDate(date) {
	if (!date) return 'n/a';
	const parsedDate = new Date(date);
	if (isNaN(parsedDate)) return date;
	return parsedDate.toLocaleString();
}

export default function Table({
	data,
	title,
	url,
	excludeList = ['_id', '__v'],
}) {
	const [sortConfig, setSortConfig] = useState({
		key: null,
		direction: 'ascending',
	});

	if (!data || data.length === 0) {
		return (
			<div className='text-gray-500 text-center p-4'>No data available</div>
		);
	}

	// Sorting logic
	const sortedData = [...data].sort((a, b) => {
		if (!sortConfig.key) return 0;
		const aVal = a[sortConfig.key];
		const bVal = b[sortConfig.key];

		return (
			(aVal < bVal ? -1 : 1) * (sortConfig.direction === 'ascending' ? 1 : -1)
		);
	});

	const headers = Object.keys(data[0]).filter(
		(key) => !excludeList.includes(key)
	);

	// Handle sorting toggle
	const handleSort = (header) => {
		setSortConfig((prev) => ({
			key: header,
			direction:
				prev.key === header && prev.direction === 'ascending'
					? 'descending'
					: 'ascending',
		}));
	};

	// Render cell content
	const renderData = (value, header, item) => {
		if (
			header.toLowerCase().includes('date') ||
			header.toLowerCase().includes('created')
		) {
			return formatDate(value);
		}
		if (header === 'img') {
			return (
				<img
					src={value}
					alt='Preview'
					className='w-10 h-10 rounded-md object-cover'
				/>
			);
		}
		if (header === 'sections') return value.length;

		if (value === null || value === undefined) return 'n/a';
		if (Array.isArray(value)) return value.join(', ');
		if (typeof value === 'boolean') return value ? 'True' : 'False';
		if (typeof value === 'object')
			return Object.entries(value)
				.map(([k, v]) => `${k}: ${v}`)
				.join(', ');

		const basePath = title.toLowerCase().includes('blog')
			? `/blog/${item._id}`
			: title.toLowerCase().includes('messages')
			? `/admin/messages/${item._id}`
			: null;

		return basePath ? (
			<Link
				to={basePath}
				className='text-blue-500 hover:underline'
			>
				{value}
			</Link>
		) : (
			value
		);
	};

	return (
		<div className='p-4 bg-white shadow rounded-lg'>
			{url ? (
				<Link
					to={url}
					className='text-lg font-semibold text-gray-900 hover:text-blue-600'
				>
					{title}
				</Link>
			) : (
				<h2 className='text-lg font-semibold text-gray-900'>{title}</h2>
			)}
			<div className='overflow-x-auto mt-4'>
				<table className='min-w-full border border-gray-200'>
					<thead className='bg-gray-100'>
						<tr>
							{headers.map((header) => (
								<th
									key={header}
									className={classNames(
										'px-4 py-2 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200',
										header.toLowerCase() === 'message' ? 'w-full' : 'w-auto'
									)}
									onClick={() => handleSort(header)}
								>
									{header}
									{sortConfig.key === header
										? sortConfig.direction === 'ascending'
											? ' ↑'
											: ' ↓'
										: ''}
								</th>
							))}
						</tr>
					</thead>
					<tbody className='divide-y divide-gray-200'>
						{sortedData.map((item, rowIndex) => (
							<tr
								key={rowIndex}
								className='hover:bg-gray-50'
							>
								{headers.map((header) => (
									<td
										key={header}
										className={classNames(
											'px-4 py-2 text-sm text-gray-700',
											header.toLowerCase() === 'message'
												? 'w-full break-words'
												: 'w-auto'
										)}
									>
										{renderData(item[header], header, item)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
